import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';
import StockDisplay from '../../components/feature/StockDisplay';

export default function ProduitBougieLivrePlie() {
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [stockInfo, setStockInfo] = useState<{available: boolean} | null>(null);

  // Définition des variantes avec SKU et configuration complète
  const variants = {
    'sans-led': {
      id: 'BOU-SANSLED-13',
      variantId: 'bougie-livre-plie-sans-led',
      name: 'Bougie sans LED',
      price: 13,
      sku: 'BOU-SANSLED-13',
      description: 'Version classique avec bougie traditionnelle',
      purchasable: true,
      visible: true,
      manage_stock: false,
      stock: null // Stock illimité
    },
    'avec-led': {
      id: 'BOU-LED-15',
      variantId: 'bougie-livre-plie-avec-led',
      name: 'Bougie avec LED',
      price: 15,
      sku: 'BOU-LED-15',
      description: 'Version moderne avec LED amovible pour une sécurité totale',
      purchasable: true,
      visible: true,
      manage_stock: false,
      stock: null // Stock illimité
    }
  };

  const currentVariant = selectedVariant ? variants[selectedVariant] : null;

  const product = {
    id: 'bougie-livre-plie',
    name: 'Bougie en livre plié',
    description: "Bougies décoratives réalisées à partir de livres anciens pliés à la main. Idéales pour une ambiance douce et poétique de Noël. Chaque pièce est unique et ornée d'un ruban délicat.",
    category: 'Collection de Noël',
    subcategory: 'Bougies de Noël',
    status: 'active',
    features: [
      'Fait main à partir de livres anciens',
      'Chaque pièce est unique',
      'Ornée d\'un ruban délicat',
      'Création artisanale française',
      'Parfait pour la décoration de Noël',
      'Ambiance douce et poétique'
    ]
  };

  const handleVariantChange = (variantKey: string) => {
    setSelectedVariant(variantKey);
    setAddedToCart(false);
  };

  const handleAddToCart = async () => {
    if (!currentVariant || !selectedVariant || !stockInfo?.available) {
      if (!selectedVariant) {
        alert('Veuillez sélectionner une variante avant d\'ajouter au panier');
      }
      return;
    }

    if (!currentVariant.purchasable) {
      alert('Cette variante n\'est pas disponible à l\'achat');
      return;
    }

    setIsAdding(true);

    try {
      // Ajouter au panier avec les bonnes informations de variante
      await addToCart({
        id: currentVariant.variantId, // Utilise l'ID de variante unique
        name: `${product.name} - ${currentVariant.name}`,
        price: currentVariant.price,
        image: '', // Image vide comme demandé
        description: `${product.description} - ${currentVariant.description}`
      }, quantity);

      // Feedback visuel de succès
      setAddedToCart(true);
      
      // Réinitialiser après 3 secondes
      setTimeout(() => {
        setAddedToCart(false);
      }, 3000);

    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      alert('Erreur lors de l\'ajout au panier. Veuillez réessayer.');
    } finally {
      setIsAdding(false);
    }
  };

  const getButtonText = () => {
    if (isAdding) return 'Ajout en cours...';
    if (addedToCart) return 'Ajouté au panier !';
    if (!selectedVariant) return 'Sélectionnez une variante';
    if (!stockInfo?.available) return 'Rupture de stock';
    return `Ajouter au panier • ${(currentVariant?.price || 0) * quantity}€`;
  };

  const isButtonDisabled = !selectedVariant || isAdding || !currentVariant?.purchasable || !stockInfo?.available;

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      <Header />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <nav className="flex items-center space-x-2 text-sm text-[#6B5E5A] mb-8">
            <a href="/" className="hover:text-[#C49B86] transition-colors" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Accueil
            </a>
            <i className="ri-arrow-right-s-line text-[#C49B86]"></i>
            <a href="/boutique" className="hover:text-[#C49B86] transition-colors" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Boutique
            </a>
            <i className="ri-arrow-right-s-line text-[#C49B86]"></i>
            <span className="text-[#C49B86] font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.name}</span>
          </nav>

          <section className="py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div className="space-y-4">
                  <div className="aspect-square bg-[#FAF9F8] rounded-lg border-2 border-[#B97A72] overflow-hidden">
                    <div className="w-full h-full flex items-center justify-center text-[#6B5E5A]">
                      <div className="text-center">
                        <i className="ri-image-line text-6xl text-[#C49B86] mb-4"></i>
                        <p className="text-lg">Espace pour vos photos</p>
                        <p className="text-sm text-[#B97A72] mt-2">Photos à ajouter manuellement</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <span className="text-sm text-[#B97A72] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {product.subcategory}
                    </span>
                    <h1 className="text-4xl font-bold text-[#6B5E5A] mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {product.name}
                    </h1>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <span className="bg-[#E8CBA8] text-white px-3 py-1 rounded-full text-sm font-medium">
                      Fait main ✋
                    </span>
                    <span className="bg-[#C49B86] text-white px-3 py-1 rounded-full text-sm font-medium">
                      Éco-responsable 🌱
                    </span>
                    <span className="bg-[#FFF8F5] text-[#C49B86] px-3 py-1 rounded-full text-sm font-medium border border-[#C49B86]">
                      Collection Noël 🎄
                    </span>
                  </div>

                  <div className="space-y-4">
                    <p className="text-lg text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {product.description}
                    </p>
                  </div>

                  {/* Sélection des variantes */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Choisissez votre variante :
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {Object.entries(variants).map(([key, variant]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => handleVariantChange(key)}
                          disabled={!variant.purchasable || !variant.visible}
                          className={`p-4 rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap ${
                            selectedVariant === key
                              ? 'border-[#B97A72] bg-[#B97A72] text-white'
                              : 'border-[#B97A72] bg-[#FAF9F8] text-[#6B5E5A] hover:bg-[#F8EDEA]'
                          } ${!variant.purchasable || !variant.visible ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <div className="text-left">
                            <div className="font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                              {variant.name}
                            </div>
                            <div className="text-sm mt-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                              {variant.description}
                            </div>
                            <div className="text-xl font-bold mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                              {variant.price}€
                            </div>
                            <div className="text-xs mt-1 opacity-75">
                              SKU: {variant.sku}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Prix affiché dynamiquement */}
                  <div className="flex items-center space-x-3">
                    {selectedVariant && currentVariant ? (
                      <span className="text-4xl font-bold text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {currentVariant.price}€
                      </span>
                    ) : (
                      <span className="text-2xl text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Sélectionnez une variante pour voir le prix
                      </span>
                    )}
                  </div>

                  {/* Affichage du stock */}
                  {selectedVariant && (
                    <StockDisplay 
                      productId={currentVariant?.variantId || product.id}
                      className="mb-4"
                    />
                  )}

                  <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg p-6 space-y-3">
                    <h3 className="text-lg font-semibold text-[#6B5E5A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Caractéristiques
                    </h3>
                    <ul className="space-y-2">
                      {product.features.map((feature, index) => (
                        <li key={index} className="flex items-center text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                          <i className="ri-check-line text-[#B97A72] mr-2"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <span className="font-medium text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>Quantité :</span>
                      <div className="flex items-center border-2 border-[#B97A72] rounded-lg">
                        <button
                          type="button"
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          className="px-3 py-2 text-[#B97A72] hover:bg-[#F8EDEA] transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-subtract-line"></i>
                        </button>
                        <span className="px-4 py-2 font-medium text-[#6B5E5A]">{quantity}</span>
                        <button
                          type="button"
                          onClick={() => setQuantity(quantity + 1)}
                          className="px-3 py-2 text-[#B97A72] hover:bg-[#F8EDEA] transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-add-line"></i>
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <button
                        type="button"
                        onClick={handleAddToCart}
                        disabled={isButtonDisabled}
                        className={`flex-1 px-8 py-4 rounded-full transition-colors whitespace-nowrap text-lg font-medium flex items-center justify-center ${
                          isButtonDisabled
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : addedToCart
                            ? 'bg-green-600 text-white'
                            : 'bg-[#B97A72] text-white hover:bg-[#C49B86] cursor-pointer'
                        }`}
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        <div className="w-5 h-5 flex items-center justify-center mr-2">
                          {isAdding ? (
                            <i className="ri-loader-4-line animate-spin"></i>
                          ) : addedToCart ? (
                            <i className="ri-check-line"></i>
                          ) : (
                            <i className="ri-shopping-cart-line"></i>
                          )}
                        </div>
                        {getButtonText()}
                      </button>
                      <a
                        href="/panier"
                        className="bg-transparent text-[#B97A72] px-8 py-4 rounded-full border-2 border-[#B97A72] hover:bg-[#B97A72] hover:text-white transition-colors whitespace-nowrap cursor-pointer text-lg font-medium flex items-center justify-center"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        <div className="w-5 h-5 flex items-center justify-center mr-2">
                          <i className="ri-shopping-bag-line"></i>
                        </div>
                        Voir le panier
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center text-green-600">
                    <i className="ri-check-line mr-2"></i>
                    <span style={{ fontFamily: 'Poppins, sans-serif' }}>En stock - Expédition sous 2-3 jours</span>
                  </div>

                  {/* Informations sur la variante sélectionnée */}
                  {selectedVariant && currentVariant && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-800 mb-2">Variante sélectionnée :</h4>
                      <div className="text-sm text-blue-700 space-y-1">
                        <p><strong>Nom :</strong> {currentVariant.name}</p>
                        <p><strong>Prix :</strong> {currentVariant.price}€</p>
                        <p><strong>SKU :</strong> {currentVariant.sku}</p>
                        <p><strong>ID Variante :</strong> {currentVariant.variantId}</p>
                        <p><strong>Disponible :</strong> {currentVariant.purchasable ? 'Oui' : 'Non'}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Section descriptive */}
          <section className="py-16 bg-[#FAF9F8] rounded-lg">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-3xl font-bold text-[#B97A72] mb-6" style={{ fontFamily: 'Allura, cursive' }}>
                Une création artisanale unique
              </h2>
              <p className="text-lg text-[#6B5E5A] leading-relaxed mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Ces bougies décoratives allient tradition artisanale et poésie de Noël. Chaque livre ancien est soigneusement 
                sélectionné puis plié page par page pour créer une forme harmonieuse et unique.
              </p>
              <p className="text-lg text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Disponibles en version classique avec bougie traditionnelle ou en version moderne avec LED amovible 
                pour une sécurité totale. Parfaites pour créer une ambiance chaleureuse dans votre intérieur pendant les fêtes.
              </p>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
